// Evidence upload: drag-and-drop on a desktop, camera on a phone.
//
// ---------------------------------------------------------------------------
// Two genuinely different interactions
// ---------------------------------------------------------------------------
//
// ON A PHONE the client is holding a receipt. They want the camera open in one
// tap, so there is a dedicated "Take a photo" button using
// `capture="environment"` — which opens the rear camera directly rather than
// a gallery picker.
//
// ON A DESKTOP the client has the document in an email or a folder, and the
// natural action is to drag it onto the question. So the whole area is a drop
// target with real feedback, and drag-and-drop is the primary path rather than
// an afterthought bolted onto a file input.
//
// Both get a normal file picker as well, because sometimes neither fits.
//
// ---------------------------------------------------------------------------
// Dragging out of an email client
// ---------------------------------------------------------------------------
//
// This is the case worth getting right, because it is what a desktop client
// will actually do, and it fails in confusing ways:
//
//   * Outlook (desktop) usually provides a real file for a dragged attachment,
//     but dragging the EMAIL ITSELF provides no file at all — just
//     proprietary clipboard formats we cannot read. Without special handling
//     the drop appears to do nothing, which reads as a broken page.
//
//   * Gmail in a browser drags attachments as a URL rather than a file, so
//     again there is no File to take.
//
// In both cases we detect "a drop with no usable files" and say what to do
// instead, rather than silently ignoring it.

import { useCallback, useId, useRef, useState } from 'react';

export interface FileDropProps {
  /** Called with whatever the client provided. Validation happens server-side. */
  onFiles: (files: File[]) => void;
  /** Guidance from the template, e.g. "Photo of the receipt is fine". */
  hint?: string | undefined;
  /** MIME types for the file picker's filter. Advisory only. */
  accept?: string[] | undefined;
  disabled?: boolean;
  /** Compact variant for inside a repeater row. */
  compact?: boolean;
}

export function FileDrop({
  onFiles,
  hint,
  accept,
  disabled = false,
  compact = false,
}: FileDropProps): JSX.Element {
  const [dragging, setDragging] = useState(false);
  const [dropProblem, setDropProblem] = useState<string | undefined>(undefined);
  const pickerRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const inputId = useId();

  // dragenter/dragleave fire for every child element the pointer crosses, so a
  // naive boolean flickers. Counting entries and exits is the standard fix.
  const dragDepth = useRef(0);

  const acceptAttr = accept?.join(',');

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      onFiles(Array.from(files));
      setDropProblem(undefined);
    },
    [onFiles],
  );

  const onDragEnter = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      if (disabled) return;
      dragDepth.current += 1;
      // Only light up for something that actually contains files. Dragging
      // selected text across the page should not look like a valid drop.
      if (event.dataTransfer.types.includes('Files')) {
        setDragging(true);
        setDropProblem(undefined);
      }
    },
    [disabled],
  );

  const onDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setDragging(false);
    }
  }, []);

  const onDragOver = useCallback(
    (event: React.DragEvent) => {
      // Both preventDefault calls are required or the browser navigates to the
      // dropped file instead of giving it to us.
      event.preventDefault();
      if (!disabled) event.dataTransfer.dropEffect = 'copy';
    },
    [disabled],
  );

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      if (disabled) return;

      const files = Array.from(event.dataTransfer.files);

      if (files.length > 0) {
        onFiles(files);
        setDropProblem(undefined);
        return;
      }

      // A drop with no files. Work out why, and say something useful — this is
      // the Outlook and Gmail case described at the top of the file.
      const types = Array.from(event.dataTransfer.types);

      if (types.includes('text/uri-list') || types.includes('text/html')) {
        setDropProblem(
          'That looks like a link rather than a file. If it came from webmail, download the attachment first, then drag it from your Downloads folder.',
        );
      } else if (types.length > 0) {
        setDropProblem(
          'We could not read that. If you dragged a whole email, drag the attachment itself instead — or save it to a folder and drag it from there.',
        );
      } else {
        setDropProblem('Nothing came through. Try the "Choose files" button instead.');
      }
    },
    [disabled, onFiles],
  );

  return (
    <div className={`filedrop ${compact ? 'filedrop--compact' : ''}`}>
      {/* The drop target is a region, not a control: the buttons inside it
          provide the keyboard and screen-reader path, so there is nothing to
          focus here and no role to assert. */}
      <div
        className={`filedrop__zone ${dragging ? 'is-dragging' : ''} ${disabled ? 'is-disabled' : ''}`}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
      >
        {dragging ? (
          <p className="filedrop__dragging">Drop to attach</p>
        ) : (
          <>
            {hint && <p className="filedrop__hint">{hint}</p>}

            <div className="filedrop__actions">
              {/*
                Camera first on mobile. `capture="environment"` opens the rear
                camera straight away rather than a gallery, which is what
                someone holding a receipt wants.

                Hidden on wider screens via CSS: a desktop has no camera worth
                photographing a document with, and the button would just be
                noise next to the drop zone.
              */}
              <button
                type="button"
                className="btn btn--soft filedrop__camera"
                onClick={() => cameraRef.current?.click()}
                disabled={disabled}
              >
                <span aria-hidden="true">📷</span> Take a photo
              </button>

              <button
                type="button"
                className="btn btn--soft"
                onClick={() => pickerRef.current?.click()}
                disabled={disabled}
              >
                <span aria-hidden="true">📎</span> Choose files
              </button>
            </div>

            {/* Only meaningful where there is a pointer to drag with. */}
            <p className="filedrop__droptext">or drag them here from your email or a folder</p>
          </>
        )}

        <input
          ref={cameraRef}
          id={`${inputId}-camera`}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          hidden
          onChange={(event) => {
            handleFiles(event.target.files);
            // Reset so choosing the same file twice still fires a change.
            event.target.value = '';
          }}
        />
        <input
          ref={pickerRef}
          id={`${inputId}-picker`}
          type="file"
          {...(acceptAttr ? { accept: acceptAttr } : {})}
          multiple
          hidden
          onChange={(event) => {
            handleFiles(event.target.files);
            event.target.value = '';
          }}
        />
      </div>

      {/* <output> is the semantic element for a message the page produces in
          response to a user action. It is announced politely by screen readers
          without us asserting role="status" by hand. */}
      {dropProblem && <output className="filedrop__problem">{dropProblem}</output>}
    </div>
  );
}
